// src/features/reception/ReceptionDashboard.tsx
import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useLocation } from 'react-router-dom';
import { format, parseISO, formatDistanceToNow } from 'date-fns';
import {
  Clock, CheckCircle, XCircle, UserCheck, RefreshCw,
  Activity, ClipboardList, Users, Search, AlertCircle,
  ChevronUp, ChevronDown, Phone, Calendar, Printer, History, Trash2,
} from 'lucide-react';
import { receptionService, queueService, doctorService, appointmentService } from '../../api/services';
import { AllAppointmentDto, AppointmentActionLogDto, AppointmentDto, ApproveRejectRequest, DoctorListDto, QueueItemDto, ReceptionMyAppointment } from '../../api/types';
import { formatAppointmentDate, formatDateTime, formatTimeOnly } from '../../utils/formatDate';
import { statusLabel } from '../../utils/statusLabel';
import { useSettingsStore } from '../settings/settings.store';
import toast from 'react-hot-toast';
import clsx from 'clsx';

type ReceptionView = 'pending' | 'queue' | 'my-appointments' | 'upcoming' | 'all';

const SOURCE_COLORS: Record<string, string> = {
  Website: 'bg-blue-50 text-blue-700',
  WalkIn: 'bg-orange-50 text-orange-700',
  WhatsApp: 'bg-green-50 text-green-700',
  Phone: 'bg-purple-50 text-purple-700',
};

const SESSION_COLORS: Record<string, string> = {
  Morning: 'bg-amber-50 text-amber-700',
  Evening: 'bg-indigo-50 text-indigo-700',
};

const REJECTION_REASONS = [
  'Doctor not available on this date',
  'Slot is fully booked',
  'Duplicate appointment found',
  'Outside consultation hours',
  'Doctor on leave',
  'Incomplete patient details',
];

const CANCELLATION_REASONS = [
  'Patient requested cancellation',
  'Patient did not show up',
  'Doctor unavailable',
  'Rescheduled to another day',
  'Duplicate appointment',
  'Emergency',
];

function useCurrentView(): ReceptionView {
  const location = useLocation();
  if (location.pathname === '/reception/queue') return 'queue';
  if (location.pathname === '/reception/my-appointments') return 'my-appointments';
  if (location.pathname === '/reception/upcoming') return 'upcoming';
  if (location.pathname === '/reception/all') return 'all';
  return 'pending';
}

const VIEW_TITLES: Record<ReceptionView, string> = {
  pending: 'Pending Approvals',
  queue: "Today's Queue",
  'my-appointments': 'My Handled Appointments',
  upcoming: 'Future Appointments',
  all: 'All Appointments',
};

function StatCard({ label, value, icon: Icon, color }: {
  label: string; value: number; icon: any; color: string;
}) {
  return (
    <div className="card p-5">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">{label}</p>
          <p className="text-3xl font-bold text-slate-900 mt-1">{value}</p>
        </div>
        <div className={clsx('w-12 h-12 rounded-xl flex items-center justify-center', color)}>
          <Icon className="w-6 h-6" />
        </div>
      </div>
    </div>
  );
}

function printTokenSlip(item: QueueItemDto, hospitalName: string) {
  const printWindow = window.open('', '_blank', 'width=794,height=1123');
  if (!printWindow) { alert('Please allow pop-ups to print token slips.'); return; }

  const apptDate = item.appointmentDate
    ? new Date(item.appointmentDate).toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })
    : 'To Be Confirmed';
  const printedAt = new Date().toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });

  printWindow.document.write(`<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8" />
  <title>Token Slip — ${item.appointmentNumber}</title>
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body {
      font-family: 'Segoe UI', Arial, sans-serif;
      width: 210mm;
      padding: 8mm 14mm 6mm;
      color: #1e293b;
    }
    .hospital {
      text-align: center;
      font-size: 18px;
      font-weight: 700;
      letter-spacing: 0.5px;
      text-transform: uppercase;
      color: #0e92e9;
      border-bottom: 2px solid #0e92e9;
      padding-bottom: 6px;
      margin-bottom: 6px;
    }
    .slip-title {
      text-align: center;
      font-size: 10px;
      font-weight: 600;
      letter-spacing: 3px;
      text-transform: uppercase;
      color: #64748b;
      margin-bottom: 8px;
    }
    .token-box {
      display: flex;
      align-items: center;
      justify-content: space-between;
      background: #f0f9ff;
      border: 2px dashed #0e92e9;
      border-radius: 8px;
      padding: 8px 16px;
      margin-bottom: 10px;
    }
    .token-left {
      text-align: left;
    }
    .token-label {
      font-size: 10px;
      font-weight: 600;
      letter-spacing: 2px;
      text-transform: uppercase;
      color: #64748b;
    }
    .token-number {
      font-size: 64px;
      font-weight: 900;
      color: #0e92e9;
      line-height: 1;
    }
    .token-right {
      text-align: right;
    }
    .token-session {
      font-size: 13px;
      font-weight: 600;
      color: #1e293b;
    }
    .token-date {
      font-size: 12px;
      color: #64748b;
      margin-top: 2px;
    }
    .details {
      border-top: 1px solid #e2e8f0;
      padding-top: 8px;
    }
    .detail-row {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 5px 0;
      border-bottom: 1px solid #f1f5f9;
      gap: 12px;
    }
    .detail-label {
      font-size: 10px;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      color: #94a3b8;
      flex-shrink: 0;
      width: 90px;
    }
    .detail-value {
      font-size: 13px;
      font-weight: 600;
      color: #1e293b;
      text-align: right;
    }
    .footer {
      text-align: center;
      margin-top: 8px;
      font-size: 9px;
      color: #94a3b8;
    }
    @media print {
      body { width: auto; }
      @page { margin: 8mm 12mm; size: A4 portrait; }
    }
  </style>
</head>
<body>
  <div class="hospital">${hospitalName}</div>
  <div class="slip-title">Appointment Token</div>

  <div class="token-box">
    <div class="token-left">
      <div class="token-label">Token No.</div>
      <div class="token-number">${item.tokenNumber ?? '–'}</div>
    </div>
    <div class="token-right">
      <div class="token-session">${item.sessionType} Session</div>
      <div class="token-date">${apptDate}</div>
    </div>
  </div>

  <div class="details">
    <div class="detail-row">
      <span class="detail-label">Appt No.</span>
      <span class="detail-value">${item.appointmentNumber}</span>
    </div>
    <div class="detail-row">
      <span class="detail-label">Patient</span>
      <span class="detail-value">${item.patientName}</span>
    </div>
    <div class="detail-row">
      <span class="detail-label">Doctor</span>
      <span class="detail-value">${item.doctorName}</span>
    </div>
    <div class="detail-row">
      <span class="detail-label">Department</span>
      <span class="detail-value">${item.departmentName}</span>
    </div>
  </div>

  <div class="footer">Printed: ${printedAt}</div>

  <!-- Cut guide at bottom of top half -->
  <div style="margin-top:12mm; border-top:1.5px dashed #cbd5e1; text-align:center; padding-top:3px;">
    <span style="font-size:9px; color:#94a3b8; letter-spacing:2px; text-transform:uppercase;">✂ &nbsp; Cut Here &nbsp; ✂</span>
  </div>

  <script>window.onload = () => { window.print(); window.close(); }<\/script>
</body>
</html>`);
  printWindow.document.close();
}

export function ReceptionDashboard() {
  const view = useCurrentView();
  // The stats strip appears on both the pending and queue views and mixes counts
  // from the pending + queue queries, so both must load whenever it is shown —
  // otherwise the "other" view's cards read 0 until that tab is visited.
  const showStats = view === 'pending' || view === 'queue';
  const qc = useQueryClient();
  const hospitalName = useSettingsStore((s) => s.hospitalName);
  const [rejectId, setRejectId] = useState<number | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [cancelId, setCancelId] = useState<number | null>(null);
  const [cancelReason, setCancelReason] = useState('');
  const [approveAppt, setApproveAppt] = useState<AppointmentDto | null>(null);
  const [approveDate, setApproveDate] = useState('');
  const [approveSession, setApproveSession] = useState<string>('');
  const [approveNotes, setApproveNotes] = useState('');
  const [editingTokenId, setEditingTokenId] = useState<number | null>(null);
  const [editingTokenValue, setEditingTokenValue] = useState('');
  const [logViewId, setLogViewId] = useState<number | null>(null);

  // Pending approvals filters
  const [pendingSearch, setPendingSearch] = useState('');
  const [pendingDeptFilter, setPendingDeptFilter] = useState('');
  const [pendingDoctorFilter, setPendingDoctorFilter] = useState('');
  const [pendingDateFilter, setPendingDateFilter] = useState('');
  const [pendingSessionFilter, setPendingSessionFilter] = useState('');

  // Queue filters
  const [queueSearch, setQueueSearch] = useState('');
  const [queueDeptFilter, setQueueDeptFilter] = useState('');
  const [queueDoctorFilter, setQueueDoctorFilter] = useState('');
  const [queueSessionFilter, setQueueSessionFilter] = useState('');

  const { data: pending = [], isLoading: pendingLoading, refetch: refetchPending } = useQuery({
    queryKey: ['reception-pending'],
    queryFn: receptionService.getPending,
    refetchInterval: 30000,
    enabled: showStats,
  });

  // Fetch doctors to identify on-call appointments
  const { data: allDoctors = [] } = useQuery({
    queryKey: ['doctors'],
    queryFn: () => doctorService.getAll(),
    enabled: view === 'pending',
  });

  const onCallDoctorIds = useMemo(
    () => new Set(allDoctors.filter((d: DoctorListDto) => d.isOnCall).map((d: DoctorListDto) => d.id)),
    [allDoctors]
  );

  const { data: queue = [], isLoading: queueLoading, refetch: refetchQueue } = useQuery({
    queryKey: ['reception-queue'],
    queryFn: queueService.getToday,
    refetchInterval: 15000,
    enabled: showStats,
  });

  const {
    data: myAppointments = [],
    isLoading: myApptLoading,
    isError: myApptError,
    refetch: refetchMyAppt,
  } = useQuery({
    queryKey: ['reception', 'my-appointments'],
    queryFn: receptionService.getMyAppointments,
    enabled: view === 'my-appointments',
  });

  const { data: upcoming = [], isLoading: upcomingLoading, refetch: refetchUpcoming } = useQuery({
    queryKey: ['reception-upcoming'],
    queryFn: receptionService.getUpcoming,
    refetchInterval: 60000,
    enabled: view === 'upcoming',
  });

  // Upcoming filters
  const [upcomingSearch, setUpcomingSearch] = useState('');
  const [upcomingDeptFilter, setUpcomingDeptFilter] = useState('');
  const [upcomingDoctorFilter, setUpcomingDoctorFilter] = useState('');
  const [upcomingStatusFilter, setUpcomingStatusFilter] = useState('');

  // All Records filters
  const [allSearch, setAllSearch] = useState('');
  const [allStatusFilter, setAllStatusFilter] = useState('');
  const [allDeptFilter, setAllDeptFilter] = useState('');
  const [allDoctorFilter, setAllDoctorFilter] = useState('');
  const [allFromDate, setAllFromDate] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - 6);
    return d.toLocaleDateString('en-CA');
  });
  const [allToDate, setAllToDate] = useState(() => new Date().toLocaleDateString('en-CA'));

  const { data: allRecords = [], isLoading: allLoading, refetch: refetchAll } = useQuery({
    queryKey: ['reception-all', allSearch, allStatusFilter, allDeptFilter, allDoctorFilter, allFromDate, allToDate],
    queryFn: () => receptionService.getAllAppointments({
      search: allSearch || undefined,
      status: allStatusFilter || undefined,
      departmentId: allDeptFilter ? parseInt(allDeptFilter) : undefined,
      doctorId: allDoctorFilter ? parseInt(allDoctorFilter) : undefined,
      from: allFromDate || undefined,
      to: allToDate || undefined,
    }),
    enabled: view === 'all',
  });

  const { mutate: approve, isPending: approving } = useMutation({
    mutationFn: ({ id, req }: { id: number; req?: ApproveRejectRequest }) =>
      receptionService.approve(id, req),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['reception-pending'] });
      qc.invalidateQueries({ queryKey: ['reception-queue'] });
      setApproveAppt(null);
      setApproveDate('');
      setApproveSession('');
      setApproveNotes('');
      toast.success(`Approved — Token #${data?.tokenNumber}`);
    },
    onError: (err: any) => toast.error(err.message),
  });

  const { mutate: reject } = useMutation({
    mutationFn: ({ id, reason }: { id: number; reason?: string }) =>
      receptionService.reject(id, { notes: reason }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['reception-pending'] });
      qc.invalidateQueries({ queryKey: ['reception-upcoming'] });
      setRejectId(null);
      setRejectReason('');
      toast.success('Appointment rejected');
    },
    onError: (err: any) => toast.error(err.message),
  });

  const { mutate: cancelAppt, isPending: cancelling } = useMutation({
    mutationFn: ({ id, reason }: { id: number; reason?: string }) =>
      receptionService.cancel(id, { notes: reason }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['reception-queue'] });
      qc.invalidateQueries({ queryKey: ['reception-pending'] });
      qc.invalidateQueries({ queryKey: ['reception-upcoming'] });
      setCancelId(null);
      setCancelReason('');
      toast.success('Appointment cancelled');
    },
    onError: (err: any) => toast.error(err.message),
  });

  const { mutate: checkIn } = useMutation({
    mutationFn: (id: number) => receptionService.checkIn(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['reception-queue'] });
      toast.success('Patient checked in');
    },
    onError: (err: any) => toast.error(err.message),
  });

  const { mutate: startConsultation } = useMutation({
    mutationFn: (id: number) => receptionService.startConsultation(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['reception-queue'] });
      toast.success('Consultation started');
    },
    onError: (err: any) => toast.error(err.message),
  });

  const { mutate: completeConsultation } = useMutation({
    mutationFn: (id: number) => receptionService.completeConsultation(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['reception-queue'] });
      toast.success('Consultation completed');
    },
    onError: (err: any) => toast.error(err.message),
  });

  const { mutate: updateToken, isPending: updatingToken } = useMutation({
    mutationFn: ({ id, token }: { id: number; token: number }) =>
      receptionService.updateToken(id, token),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['reception-queue'] });
      setEditingTokenId(null);
      setEditingTokenValue('');
      if (data?.swapped) {
        toast.success(`Swapped! Token #${data.updated.tokenNumber} ↔ ${data.swapped.patientName} (Token #${data.swapped.tokenNumber})`);
      } else {
        toast.success(`Token updated to #${data?.updated.tokenNumber}`);
      }
    },
    onError: (err: any) => toast.error(err.message),
  });

  const { mutate: removeToken, isPending: removingToken } = useMutation({
    mutationFn: (id: number) => receptionService.removeToken(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['reception-queue'] });
      setEditingTokenId(null);
      setEditingTokenValue('');
      toast.success('Token removed');
    },
    onError: (err: any) => toast.error(err.message),
  });

  // Immediate-save on Enter / blur — no confirm buttons. Silently exits edit
  // mode when the value is empty, invalid, or unchanged.
  const commitTokenEdit = (item: QueueItemDto) => {
    const newToken = parseInt(editingTokenValue.trim(), 10);
    if (isNaN(newToken) || newToken < 1 || newToken === item.tokenNumber) {
      setEditingTokenId(null);
      setEditingTokenValue('');
      return;
    }
    updateToken({ id: item.appointmentId, token: newToken });
  };

  const queueStats = {
    total: queue.length,
    checkedIn: queue.filter((q) => q.status === 'CHECKED_IN').length,
    inConsult: queue.filter((q) => q.status === 'IN_CONSULTATION').length,
    completed: queue.filter((q) => q.status === 'COMPLETED').length,
  };

  // Unique filter options derived from live data
  const pendingDepts = useMemo(() => [...new Set(pending.map(a => a.departmentName))].sort(), [pending]);
  const pendingDoctors = useMemo(() => [...new Set(pending.map(a => a.doctorName))].sort(), [pending]);
  const queueDepts = useMemo(() => [...new Set(queue.map(i => i.departmentName))].sort(), [queue]);
  const queueDoctors = useMemo(() => [...new Set(queue.map(i => i.doctorName))].sort(), [queue]);

  const filteredPending = useMemo(() => {
    let result = [...pending];
    if (pendingSearch.trim()) {
      const q = pendingSearch.toLowerCase();
      result = result.filter(a =>
        a.patientName.toLowerCase().includes(q) ||
        a.appointmentNumber.toLowerCase().includes(q) ||
        a.doctorName.toLowerCase().includes(q)
      );
    }
    if (pendingDeptFilter) result = result.filter(a => a.departmentName === pendingDeptFilter);
    if (pendingDoctorFilter) result = result.filter(a => a.doctorName === pendingDoctorFilter);
    if (pendingDateFilter) result = result.filter(a => a.appointmentDate === pendingDateFilter);
    if (pendingSessionFilter) result = result.filter(a => a.sessionType === pendingSessionFilter);
    return result;
  }, [pending, pendingSearch, pendingDeptFilter, pendingDoctorFilter, pendingDateFilter, pendingSessionFilter]);

  const filteredQueue = useMemo(() => {
    let result = [...queue];
    if (queueSearch.trim()) {
      const q = queueSearch.toLowerCase();
      result = result.filter(i =>
        i.patientName.toLowerCase().includes(q) ||
        i.appointmentNumber.toLowerCase().includes(q)
      );
    }
    if (queueDeptFilter) result = result.filter(i => i.departmentName === queueDeptFilter);
    if (queueDoctorFilter) result = result.filter(i => i.doctorName === queueDoctorFilter);
    if (queueSessionFilter) result = result.filter(i => i.sessionType === queueSessionFilter);
    return result;
  }, [queue, queueSearch, queueDeptFilter, queueDoctorFilter, queueSessionFilter]);

  const upcomingDepts = useMemo(() => [...new Set(upcoming.map(a => a.departmentName))].sort(), [upcoming]);
  const upcomingDoctors = useMemo(() => [...new Set(upcoming.map(a => a.doctorName))].sort(), [upcoming]);

  const filteredUpcoming = useMemo(() => {
    let result = [...upcoming];
    if (upcomingSearch.trim()) {
      const q = upcomingSearch.toLowerCase();
      result = result.filter(a =>
        a.patientName.toLowerCase().includes(q) ||
        a.appointmentNumber.toLowerCase().includes(q) ||
        a.doctorName.toLowerCase().includes(q)
      );
    }
    if (upcomingDeptFilter) result = result.filter(a => a.departmentName === upcomingDeptFilter);
    if (upcomingDoctorFilter) result = result.filter(a => a.doctorName === upcomingDoctorFilter);
    if (upcomingStatusFilter) result = result.filter(a => a.status === upcomingStatusFilter);
    return result;
  }, [upcoming, upcomingSearch, upcomingDeptFilter, upcomingDoctorFilter, upcomingStatusFilter]);

  const handleRefresh = () => {
    if (view === 'pending') refetchPending();
    else if (view === 'queue') refetchQueue();
    else if (view === 'my-appointments') refetchMyAppt();
    else if (view === 'upcoming') refetchUpcoming();
  };

  return (
    <div className="max-w-6xl mx-auto px-6 py-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2">
            <ClipboardList className="w-6 h-6 text-brand-600" />
            {VIEW_TITLES[view]}
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            {format(new Date(), 'EEEE, MMMM d yyyy')}
          </p>
        </div>
        <button onClick={handleRefresh} className="btn btn-secondary btn-sm">
          <RefreshCw className="w-3.5 h-3.5" />
          Refresh
        </button>
      </div>

      {/* Stats — shown on pending and queue views */}
      {showStats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <StatCard label="Pending Approval" value={pending.length} icon={Clock} color="bg-amber-100 text-amber-600" />
          <StatCard label="Today's Queue" value={queueStats.total} icon={Users} color="bg-brand-100 text-brand-600" />
          <StatCard label="Checked In" value={queueStats.checkedIn} icon={UserCheck} color="bg-blue-100 text-blue-600" />
          <StatCard label="Completed" value={queueStats.completed} icon={CheckCircle} color="bg-emerald-100 text-emerald-600" />
        </div>
      )}

      {/* ── Pending Approvals ─────────────────────────────────────────── */}
      {view === 'pending' && (
        <div className="space-y-3">
          {/* Filter bar */}
          {!pendingLoading && pending.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-2">
              <div className="relative">
                <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                <input
                  type="search"
                  placeholder="Search patient, doctor…"
                  value={pendingSearch}
                  onChange={e => setPendingSearch(e.target.value)}
                  className="input pl-10 py-2.5 text-base h-12 w-56"
                />
              </div>
              <select value={pendingDeptFilter} onChange={e => setPendingDeptFilter(e.target.value)} className="input py-2.5 text-base h-12 w-40">
                <option value="">All Specialities</option>
                {pendingDepts.map(d => <option key={d} value={d}>{d}</option>)}
              </select>
              <select value={pendingDoctorFilter} onChange={e => setPendingDoctorFilter(e.target.value)} className="input py-2.5 text-base h-12 w-40">
                <option value="">All Doctors</option>
                {pendingDoctors.map(d => <option key={d} value={d}>{d}</option>)}
              </select>
              <input
                type="date"
                value={pendingDateFilter}
                onChange={e => setPendingDateFilter(e.target.value)}
                className="input py-2.5 text-base h-12 w-40"
              />
              <select value={pendingSessionFilter} onChange={e => setPendingSessionFilter(e.target.value)} className="input py-2.5 text-base h-12 w-40">
                <option value="">All Sessions</option>
                <option value="Morning">Morning</option>
                <option value="Evening">Evening</option>
              </select>
              {(pendingSearch || pendingDeptFilter || pendingDoctorFilter || pendingDateFilter || pendingSessionFilter) && (
                <button
                  onClick={() => { setPendingSearch(''); setPendingDeptFilter(''); setPendingDoctorFilter(''); setPendingDateFilter(''); setPendingSessionFilter(''); }}
                  className="btn btn-ghost btn-sm text-xs text-slate-500"
                >
                  Clear filters
                </button>
              )}
              <span className="ml-auto text-xs text-slate-500 self-center">
                {filteredPending.length} of {pending.length}
              </span>
            </div>
          )}
          {pendingLoading ? (
            [1, 2, 3].map((i) => <div key={i} className="skeleton h-24 rounded-xl" />)
          ) : pending.length === 0 ? (
            <div className="text-center py-16">
              <CheckCircle className="w-10 h-10 text-emerald-400 mx-auto mb-3" />
              <p className="text-slate-500">No pending appointments. All clear!</p>
            </div>
          ) : filteredPending.length === 0 ? (
            <div className="text-center py-12">
              <Search className="w-8 h-8 text-slate-300 mx-auto mb-2" />
              <p className="text-slate-500">No appointments match the selected filters</p>
            </div>
          ) : (
            filteredPending.map((appt: AppointmentDto) => (
              <div key={appt.id} className="card p-5 hover:shadow-card-hover transition-all animate-fade-up">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 flex-wrap">
                      <span className="font-mono text-xs font-semibold text-brand-700 bg-brand-50 px-2 py-0.5 rounded">
                        {appt.appointmentNumber}
                      </span>
                      <span className={`status-${appt.status}`}>{statusLabel(appt.status)}</span>
                      {appt.isFollowUp && (
                        <span className="badge bg-purple-100 text-purple-700">Follow-up</span>
                      )}
                      <span className="text-xs text-slate-400">
                        via {appt.bookingSource}
                      </span>
                      {onCallDoctorIds.has(appt.doctorId) && (
                        <span className="flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 font-medium">
                          <Phone className="w-3 h-3" />
                          Call doctor to confirm
                        </span>
                      )}
                    </div>

                    <div className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                      <div>
                        <div className="text-xs text-slate-400 font-medium">Patient</div>
                        <div className="font-semibold text-slate-900">{appt.patientName}</div>
                        <div className="text-xs text-slate-500">{appt.patientPhone}</div>
                      </div>
                      <div>
                        <div className="text-xs text-slate-400 font-medium">Doctor</div>
                        <div className="font-medium text-slate-800">{appt.doctorName}</div>
                        <div className="text-xs text-slate-500">{appt.departmentName}</div>
                      </div>
                      <div>
                        <div className="text-xs text-slate-400 font-medium">Date</div>
                        {appt.appointmentDate ? (
                          <>
                            <div className="font-medium text-slate-800">
                              {format(parseISO(appt.appointmentDate), 'EEE, MMM d')}
                            </div>
                            <div className="text-xs text-slate-500">{appt.sessionType}</div>
                          </>
                        ) : (
                          <div className="text-xs text-amber-600 font-medium">To be confirmed</div>
                        )}
                      </div>
                      <div>
                        <div className="text-xs text-slate-400 font-medium">Requested</div>
                        <div className="font-medium text-slate-800" title={format(parseISO(appt.createdAt), 'MMM d, yyyy h:mm a')}>
                          {formatDistanceToNow(parseISO(appt.createdAt), { addSuffix: true })}
                        </div>
                      </div>
                    </div>

                    {appt.patientNotes && (
                      <div className="mt-2 p-2 bg-slate-50 rounded-lg text-xs text-slate-600 border border-slate-100">
                        {appt.patientNotes}
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex flex-col gap-2 flex-shrink-0">
                    <button
                      onClick={() => {
                        if (appt.isOnCallBooking) {
                          setApproveAppt(appt);
                        } else {
                          approve({ id: appt.id });
                        }
                      }}
                      disabled={approving}
                      className="btn btn-sm bg-emerald-600 hover:bg-emerald-700 text-white whitespace-nowrap"
                    >
                      <CheckCircle className="w-3.5 h-3.5" />
                      Approve
                    </button>
                    <button
                      onClick={() => setRejectId(appt.id)}
                      className="btn btn-sm bg-red-50 hover:bg-red-100 text-red-700 border border-red-200"
                    >
                      <XCircle className="w-3.5 h-3.5" />
                      Reject
                    </button>
                    <button
                      onClick={() => setLogViewId(appt.id)}
                      className="btn btn-sm btn-ghost text-slate-500"
                      title="View activity log"
                    >
                      <History className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* ── Today's Queue ─────────────────────────────────────────────── */}
      {view === 'queue' && (
        <div className="space-y-3">
          {/* Filter bar */}
          {!queueLoading && queue.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-2">
              <div className="relative">
                <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                <input
                  type="search"
                  placeholder="Search patient…"
                  value={queueSearch}
                  onChange={e => setQueueSearch(e.target.value)}
                  className="input pl-10 py-2.5 text-base h-12 w-96"
                />
              </div>
              <select value={queueDeptFilter} onChange={e => setQueueDeptFilter(e.target.value)} className="input py-2.5 text-base h-12 w-56">
                <option value="">All Specialities</option>
                {queueDepts.map(d => <option key={d} value={d}>{d}</option>)}
              </select>
              <select value={queueDoctorFilter} onChange={e => setQueueDoctorFilter(e.target.value)} className="input py-2.5 text-base h-12 w-56">
                <option value="">All Doctors</option>
                {queueDoctors.map(d => <option key={d} value={d}>{d}</option>)}
              </select>
              <select value={queueSessionFilter} onChange={e => setQueueSessionFilter(e.target.value)} className="input py-2.5 text-base h-12 w-48">
                <option value="">All Sessions</option>
                <option value="Morning">Morning</option>
                <option value="Evening">Evening</option>
              </select>
              {(queueSearch || queueDeptFilter || queueDoctorFilter || queueSessionFilter) && (
                <button
                  onClick={() => { setQueueSearch(''); setQueueDeptFilter(''); setQueueDoctorFilter(''); setQueueSessionFilter(''); }}
                  className="btn btn-ghost btn-sm text-xs text-slate-500"
                >
                  Clear filters
                </button>
              )}
              <span className="ml-auto text-xs text-slate-500 self-center">
                {filteredQueue.length} of {queue.length}
              </span>
            </div>
          )}
          {queueLoading ? (
            [1, 2, 3].map((i) => <div key={i} className="skeleton h-20 rounded-xl" />)
          ) : queue.length === 0 ? (
            <div className="text-center py-16">
              <Users className="w-10 h-10 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">No appointments in today's queue</p>
            </div>
          ) : filteredQueue.length === 0 ? (
            <div className="text-center py-12">
              <Search className="w-8 h-8 text-slate-300 mx-auto mb-2" />
              <p className="text-slate-500">No patients match the selected filters</p>
            </div>
          ) : (
            filteredQueue.map((item: QueueItemDto) => (
              <div key={item.appointmentId} className="card p-4 hover:shadow-card-hover transition-all animate-fade-up">
                <div className="flex items-center gap-4">
                  {/* Token badge — click to edit */}
                  {editingTokenId === item.appointmentId ? (
                    <div className="flex flex-col items-center gap-1 flex-shrink-0">
                      <input
                        type="number"
                        min={1}
                        autoFocus
                        value={editingTokenValue}
                        onChange={(e) => setEditingTokenValue(e.target.value)}
                        onBlur={() => commitTokenEdit(item)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') commitTokenEdit(item);
                          if (e.key === 'Escape') { setEditingTokenId(null); setEditingTokenValue(''); }
                        }}
                        className="w-16 text-center font-bold text-brand-700 border-2 border-brand-400 rounded-lg px-1 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-brand-400"
                        disabled={updatingToken || removingToken}
                      />
                      {item.tokenNumber != null && (
                        <button
                          // preventDefault keeps the input's onBlur from firing a
                          // stray save before this click removes the token
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => removeToken(item.appointmentId)}
                          disabled={removingToken}
                          className="flex items-center gap-1 text-xs px-2 py-0.5 bg-red-50 text-red-600 rounded hover:bg-red-100 disabled:opacity-50"
                          title="Remove assigned token"
                        >
                          <Trash2 className="w-3 h-3" /> Delete
                        </button>
                      )}
                    </div>
                  ) : (
                    <div
                      onClick={() => {
                        setEditingTokenId(item.appointmentId);
                        setEditingTokenValue(item.tokenNumber?.toString() ?? '');
                      }}
                      title="Click to change token number"
                      className={clsx(
                        'w-12 h-12 rounded-xl flex items-center justify-center text-lg font-bold flex-shrink-0 cursor-pointer group relative',
                        item.tokenNumber ? 'bg-brand-100 text-brand-700 hover:bg-brand-200' : 'bg-slate-100 text-slate-400 hover:bg-slate-200'
                      )}
                    >
                      {item.tokenNumber ?? '–'}
                      <span className="absolute -top-1 -right-1 w-4 h-4 bg-slate-500 text-white rounded-full text-[9px] flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">✎</span>
                    </div>
                  )}


                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-slate-900">{item.patientName}</span>
                      <span className={`status-${item.status}`}>{statusLabel(item.status)}</span>
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      {item.doctorName} · {item.departmentName} · {item.sessionType}
                    </div>
                    <div className="text-xs text-slate-400 mt-0.5 flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {item.appointmentDate
                        ? format(parseISO(item.appointmentDate), 'EEE, MMM d')
                        : 'Date TBC'}
                      <span className="text-slate-300">·</span>
                      Booked {format(parseISO(item.createdAt), 'h:mm a, MMM d')}
                    </div>
                    {item.checkedInAt && (
                      <div className="text-xs text-slate-400 mt-0.5">
                        Checked in at {format(parseISO(item.checkedInAt), 'h:mm a')}
                      </div>
                    )}
                  </div>

                  <div className="flex items-center gap-2 flex-shrink-0">
                    {item.status === 'CONFIRMED' && (
                      <button
                        onClick={() => checkIn(item.appointmentId)}
                        className="btn btn-primary btn-sm"
                      >
                        <UserCheck className="w-3.5 h-3.5" />
                        Check In
                      </button>
                    )}
                    {item.status === 'CHECKED_IN' && (
                      <button
                        onClick={() => startConsultation(item.appointmentId)}
                        className="btn btn-sm bg-purple-600 text-white hover:bg-purple-700"
                      >
                        <Activity className="w-3.5 h-3.5" />
                        Start
                      </button>
                    )}
                    {item.status === 'IN_CONSULTATION' && (
                      <button
                        onClick={() => completeConsultation(item.appointmentId)}
                        className="btn btn-sm bg-emerald-600 text-white hover:bg-emerald-700"
                      >
                        <CheckCircle className="w-3.5 h-3.5" />
                        Complete
                      </button>
                    )}
                    {item.status !== 'COMPLETED' && (
                      <button
                        onClick={() => { setCancelId(item.appointmentId); setCancelReason(''); }}
                        className="btn btn-sm bg-red-50 hover:bg-red-100 text-red-700 border border-red-200"
                        title="Cancel this appointment"
                      >
                        <XCircle className="w-3.5 h-3.5" />
                        Cancel
                      </button>
                    )}
                    <button
                      onClick={() => printTokenSlip(item, hospitalName)}
                      className="btn btn-sm btn-secondary"
                      title="Print token slip"
                    >
                      <Printer className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => setLogViewId(item.appointmentId)}
                      className="btn btn-sm btn-ghost text-slate-500"
                      title="View activity log"
                    >
                      <History className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* ── My Handled Appointments ───────────────────────────────────── */}
      {view === 'my-appointments' && (
        <MyAppointmentsSection
          data={myAppointments}
          isLoading={myApptLoading}
          isError={myApptError}
          onRetry={refetchMyAppt}
        />
      )}

      {/* ── Upcoming Appointments ──────────────────────────────────────── */}
      {view === 'upcoming' && (
        <div className="space-y-4">
          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <StatCard label="Total Upcoming" value={upcoming.length} icon={Calendar} color="bg-brand-100 text-brand-600" />
            <StatCard label="Confirmed" value={upcoming.filter(a => a.status === 'CONFIRMED').length} icon={CheckCircle} color="bg-emerald-100 text-emerald-600" />
            <StatCard label="Pending" value={upcoming.filter(a => a.status === 'PENDING').length} icon={Clock} color="bg-amber-100 text-amber-600" />
          </div>

          {/* Filters */}
          {upcoming.length > 0 && (
            <div className="card p-3">
              <div className="flex flex-wrap gap-2 mb-2">
                <div className="relative">
                  <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input type="search" placeholder="Search..." className="input pl-10 w-56 text-base h-12 py-2.5"
                    value={upcomingSearch} onChange={e => setUpcomingSearch(e.target.value)} />
                </div>
                <select className="input text-base h-12 w-40 py-2.5" value={upcomingDeptFilter} onChange={e => setUpcomingDeptFilter(e.target.value)}>
                  <option value="">All Departments</option>
                  {upcomingDepts.map(d => <option key={d} value={d}>{d}</option>)}
                </select>
                <select className="input text-base h-12 w-40 py-2.5" value={upcomingDoctorFilter} onChange={e => setUpcomingDoctorFilter(e.target.value)}>
                  <option value="">All Doctors</option>
                  {upcomingDoctors.map(d => <option key={d} value={d}>{d}</option>)}
                </select>
                <select className="input text-base h-12 w-40 py-2.5" value={upcomingStatusFilter} onChange={e => setUpcomingStatusFilter(e.target.value)}>
                  <option value="">All Status</option>
                  <option value="CONFIRMED">Confirmed</option>
                  <option value="PENDING">Pending</option>
                </select>
              </div>
              <div className="text-xs text-slate-500">{filteredUpcoming.length} of {upcoming.length}</div>
            </div>
          )}

          {/* List */}
          {upcomingLoading ? (
            <div className="space-y-3">{[1,2,3].map(i => <div key={i} className="skeleton h-24 rounded-xl" />)}</div>
          ) : filteredUpcoming.length === 0 ? (
            <div className="card p-12 text-center">
              <Calendar className="w-10 h-10 text-slate-300 mx-auto mb-3" />
              <p className="text-sm text-slate-500">No upcoming appointments</p>
            </div>
          ) : (
            filteredUpcoming.map((appt: AppointmentDto) => (
              <div key={appt.id} className="card p-4 hover:shadow-card-hover transition-shadow">
                <div className="flex items-center gap-4">
                  <div className={clsx(
                    'w-12 h-12 rounded-xl flex items-center justify-center text-lg font-bold flex-shrink-0',
                    appt.tokenNumber ? 'bg-brand-100 text-brand-700' : 'bg-slate-100 text-slate-400'
                  )}>
                    {appt.tokenNumber ?? '–'}
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-slate-900">{appt.patientName}</span>
                      <span className={`status-${appt.status}`}>{statusLabel(appt.status)}</span>
                      {appt.isOnCallBooking && (
                        <span className="text-[10px] px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded-full font-medium">On-Call</span>
                      )}
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      {appt.doctorName} · {appt.departmentName}
                      {appt.sessionType && ` · ${appt.sessionType}`}
                    </div>
                    <div className="flex items-center gap-3 text-xs text-slate-400 mt-1">
                      <span className="font-mono">{appt.appointmentNumber}</span>
                      <span>{appt.appointmentDate ? format(parseISO(appt.appointmentDate), 'EEE, MMM d yyyy') : 'TBC'}</span>
                      <span title={format(parseISO(appt.createdAt), 'MMM d, yyyy h:mm a')}>
                        Booked {formatDistanceToNow(parseISO(appt.createdAt), { addSuffix: true })}
                      </span>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 flex-shrink-0">
                    <button
                      onClick={() => { setRejectId(appt.id); setRejectReason(''); }}
                      className="btn btn-danger btn-sm"
                    >
                      <XCircle className="w-3.5 h-3.5" />
                      Reject
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* On-call approve dialog — assign date & session */}
      {approveAppt && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm">
          <div className="card w-full max-w-md mx-4 p-6 shadow-floating animate-slide-in">
            <h3 className="font-semibold text-slate-900 mb-1">Confirm On-Call Appointment</h3>
            <p className="text-xs text-slate-500 mb-4">
              Call the doctor, then assign the confirmed date and session below.
            </p>

            <div className="p-3 bg-amber-50 rounded-lg border border-amber-200 mb-4 text-xs text-amber-700">
              <div className="font-medium mb-1">On-Call Booking</div>
              <div>{approveAppt.patientName} → {approveAppt.doctorName}</div>
              <div className="text-amber-600 mt-0.5">{approveAppt.patientPhone}</div>
            </div>

            <div className="space-y-3 mb-4">
              <div>
                <label className="label">Appointment Date *</label>
                <input
                  type="date"
                  className="input"
                  value={approveDate}
                  onChange={(e) => setApproveDate(e.target.value)}
                  min={format(new Date(), 'yyyy-MM-dd')}
                />
              </div>
              <div>
                <label className="label">Session *</label>
                <select
                  className="input"
                  value={approveSession}
                  onChange={(e) => setApproveSession(e.target.value)}
                >
                  <option value="">Select session…</option>
                  <option value="Morning">Morning</option>
                  <option value="Evening">Evening</option>
                </select>
              </div>
              <div>
                <label className="label">Notes (optional)</label>
                <input
                  className="input"
                  value={approveNotes}
                  onChange={(e) => setApproveNotes(e.target.value)}
                  placeholder="e.g. Confirmed with Dr. Kumar"
                />
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => { setApproveAppt(null); setApproveDate(''); setApproveSession(''); setApproveNotes(''); }}
                className="btn btn-secondary flex-1 justify-center"
              >
                Cancel
              </button>
              <button
                onClick={() => approve({
                  id: approveAppt.id,
                  req: {
                    appointmentDate: approveDate,
                    sessionType: approveSession,
                    notes: approveNotes || undefined,
                  },
                })}
                disabled={!approveDate || !approveSession || approving}
                className="btn bg-emerald-600 hover:bg-emerald-700 text-white flex-1 justify-center"
              >
                {approving ? 'Confirming…' : 'Confirm Appointment'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── All Records ───────────────────────────────────────────────── */}
      {view === 'all' && (
        <AllRecordsSection
          data={allRecords as AllAppointmentDto[]}
          isLoading={allLoading}
          onRefresh={refetchAll}
          search={allSearch} onSearchChange={setAllSearch}
          statusFilter={allStatusFilter} onStatusChange={setAllStatusFilter}
          deptFilter={allDeptFilter} onDeptChange={setAllDeptFilter}
          doctorFilter={allDoctorFilter} onDoctorChange={setAllDoctorFilter}
          fromDate={allFromDate} onFromDateChange={setAllFromDate}
          toDate={allToDate} onToDateChange={setAllToDate}
          onViewLog={(id) => setLogViewId(id)}
        />
      )}

      {/* Action Log modal */}
      {logViewId && (
        <AppointmentLogModal appointmentId={logViewId} onClose={() => setLogViewId(null)} />
      )}

      {/* Reject dialog */}
      {rejectId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm">
          <div className="card w-full max-w-md mx-4 p-6 shadow-floating animate-slide-in">
            <h3 className="font-semibold text-slate-900 mb-1">Reject Appointment</h3>
            <p className="text-xs text-slate-500 mb-3">Select a reason or write a custom one.</p>
            <div className="flex flex-wrap gap-2 mb-3">
              {REJECTION_REASONS.map((reason) => (
                <button
                  key={reason}
                  type="button"
                  onClick={() => setRejectReason(reason)}
                  className={clsx(
                    'text-xs px-2.5 py-1 rounded-full border transition-all',
                    rejectReason === reason
                      ? 'bg-red-100 border-red-300 text-red-700 font-medium'
                      : 'bg-slate-50 border-slate-200 text-slate-600 hover:border-red-200 hover:text-red-600'
                  )}
                >
                  {reason}
                </button>
              ))}
            </div>
            <textarea
              className="input resize-none h-20 mb-4"
              placeholder="Or write a custom reason (optional)…"
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
            />
            <div className="flex gap-3">
              <button onClick={() => { setRejectId(null); setRejectReason(''); }} className="btn btn-secondary flex-1 justify-center">
                Cancel
              </button>
              <button
                onClick={() => reject({ id: rejectId, reason: rejectReason || undefined })}
                className="btn btn-danger flex-1 justify-center"
              >
                Reject
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cancel dialog — cancel a confirmed/queued appointment from Today's Queue */}
      {cancelId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm">
          <div className="card w-full max-w-md mx-4 p-6 shadow-floating animate-slide-in">
            <h3 className="font-semibold text-slate-900 mb-1">Cancel Appointment</h3>
            <p className="text-xs text-slate-500 mb-3">Select a reason or write a custom one.</p>
            <div className="flex flex-wrap gap-2 mb-3">
              {CANCELLATION_REASONS.map((reason) => (
                <button
                  key={reason}
                  type="button"
                  onClick={() => setCancelReason(reason)}
                  className={clsx(
                    'text-xs px-2.5 py-1 rounded-full border transition-all',
                    cancelReason === reason
                      ? 'bg-red-100 border-red-300 text-red-700 font-medium'
                      : 'bg-slate-50 border-slate-200 text-slate-600 hover:border-red-200 hover:text-red-600'
                  )}
                >
                  {reason}
                </button>
              ))}
            </div>
            <textarea
              className="input resize-none h-20 mb-4"
              placeholder="Or write a custom reason (optional)…"
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
            />
            <div className="flex gap-3">
              <button onClick={() => { setCancelId(null); setCancelReason(''); }} className="btn btn-secondary flex-1 justify-center">
                Keep Appointment
              </button>
              <button
                onClick={() => cancelAppt({ id: cancelId, reason: cancelReason || undefined })}
                disabled={cancelling}
                className="btn btn-danger flex-1 justify-center disabled:opacity-50"
              >
                Cancel Appointment
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Appointment Action Log Modal ─────────────────────────────────────────

const ACTION_LABELS: Record<string, { label: string; color: string }> = {
  BOOKED:                  { label: 'Booked',              color: 'bg-brand-100 text-brand-700' },
  WALKIN_BOOKED:           { label: 'Walk-in',             color: 'bg-orange-100 text-orange-700' },
  APPROVED:                { label: 'Approved',            color: 'bg-emerald-100 text-emerald-700' },
  REJECTED:                { label: 'Rejected',            color: 'bg-red-100 text-red-700' },
  CHECKED_IN:              { label: 'Checked In',          color: 'bg-sky-100 text-sky-700' },
  CONSULTATION_STARTED:    { label: 'Consultation Started',color: 'bg-purple-100 text-purple-700' },
  CONSULTATION_COMPLETED:  { label: 'Completed',           color: 'bg-emerald-100 text-emerald-700' },
  CANCELLED:               { label: 'Cancelled',           color: 'bg-slate-100 text-slate-600' },
  TOKEN_UPDATED:           { label: 'Token Changed',       color: 'bg-amber-100 text-amber-700' },
  TOKEN_REMOVED:           { label: 'Token Removed',       color: 'bg-red-100 text-red-700' },
};

function AppointmentLogModal({ appointmentId, onClose }: { appointmentId: number; onClose: () => void }) {
  const { data: logs = [], isLoading } = useQuery({
    queryKey: ['appointment-logs', appointmentId],
    queryFn: () => appointmentService.getActionLogs(appointmentId),
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm" onClick={onClose}>
      <div
        className="card w-full max-w-md mx-4 p-6 shadow-floating animate-slide-in max-h-[80vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-slate-900 flex items-center gap-2">
            <History className="w-4 h-4 text-slate-500" />
            Activity Log
          </h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 text-xl leading-none">×</button>
        </div>

        <div className="overflow-y-auto flex-1">
          {isLoading ? (
            <div className="space-y-3">
              {[1, 2, 3].map(i => <div key={i} className="skeleton h-14 rounded-lg" />)}
            </div>
          ) : logs.length === 0 ? (
            <p className="text-center text-slate-400 py-8 text-sm">No activity recorded yet.</p>
          ) : (
            <ol className="relative border-l border-slate-200 ml-3 space-y-5">
              {(logs as AppointmentActionLogDto[]).map((log, i) => {
                const meta = ACTION_LABELS[log.action] ?? { label: log.action, color: 'bg-slate-100 text-slate-600' };
                return (
                  <li key={log.id} className={`ml-4 ${i === 0 ? 'pt-0' : ''}`}>
                    <span className="absolute -left-1.5 w-3 h-3 rounded-full bg-slate-300 border-2 border-white" />
                    <div className="flex items-start gap-2 flex-wrap">
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${meta.color}`}>{meta.label}</span>
                      <span className="text-xs text-slate-400 mt-0.5">
                        {format(parseISO(log.createdAt), 'MMM d, h:mm a')}
                      </span>
                    </div>
                    {(log.performedBy || log.performedByRole) && (
                      <p className="text-xs text-slate-500 mt-0.5">
                        by {log.performedBy ?? ''}{log.performedByRole ? ` (${log.performedByRole})` : ''}
                      </p>
                    )}
                    {log.notes && (
                      <p className="text-xs text-slate-600 mt-0.5 italic">"{log.notes}"</p>
                    )}
                  </li>
                );
              })}
            </ol>
          )}
        </div>
      </div>
    </div>
  );
}

// ── My Handled Appointments Section ──────────────────────────────────────

type SortField = 'appointmentDate' | 'status' | 'reviewedAt';
type SortDir = 'asc' | 'desc';

function MyAppointmentsSection({
  data,
  isLoading,
  isError,
  onRetry,
}: {
  data: ReceptionMyAppointment[];
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
}) {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('All');
  const [sortField, setSortField] = useState<SortField>('reviewedAt');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('desc');
    }
  };

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) return <ChevronDown className="w-3 h-3 opacity-30" />;
    return sortDir === 'asc'
      ? <ChevronUp className="w-3 h-3" />
      : <ChevronDown className="w-3 h-3" />;
  };

  const filtered = useMemo(() => {
    let result = [...data];

    if (statusFilter !== 'All') {
      result = result.filter(a => a.status === statusFilter);
    }

    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(a =>
        a.patientName.toLowerCase().includes(q) ||
        a.appointmentNumber.toLowerCase().includes(q)
      );
    }

    result.sort((a, b) => {
      let cmp = 0;
      if (sortField === 'appointmentDate') {
        cmp = (a.appointmentDate ?? '').localeCompare(b.appointmentDate ?? '');
      } else if (sortField === 'status') {
        cmp = a.status.localeCompare(b.status);
      } else if (sortField === 'reviewedAt') {
        cmp = (a.reviewedAt ?? '').localeCompare(b.reviewedAt ?? '');
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });

    return result;
  }, [data, search, statusFilter, sortField, sortDir]);

  if (isLoading) {
    return (
      <div className="space-y-3">
        {[1, 2, 3].map(i => <div key={i} className="skeleton h-12 rounded-xl" />)}
      </div>
    );
  }

  if (isError) {
    return (
      <div className="text-center py-16">
        <AlertCircle className="w-10 h-10 text-red-300 mx-auto mb-3" />
        <p className="text-slate-500 mb-3">Failed to load appointments</p>
        <button onClick={onRetry} className="btn btn-secondary btn-sm">
          <RefreshCw className="w-3.5 h-3.5" />
          Retry
        </button>
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div className="text-center py-16">
        <ClipboardList className="w-10 h-10 text-slate-300 mx-auto mb-3" />
        <p className="text-slate-500">No appointments handled yet</p>
      </div>
    );
  }

  return (
    <>
      {/* Filters bar */}
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <div className="relative">
          <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
          <input
            type="search"
            placeholder="Search patient or appt #…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="input pl-10 py-2.5 text-base h-12 w-56"
          />
        </div>
        <select
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value)}
          className="input py-2.5 text-base h-12 w-40"
        >
          <option value="All">All statuses</option>
          <option value="CONFIRMED">Confirmed</option>
          <option value="REJECTED">Rejected</option>
          <option value="COMPLETED">Completed</option>
          <option value="CANCELLED">Cancelled</option>
          <option value="CHECKED_IN">Checked In</option>
        </select>
        <span className="text-sm text-slate-500 ml-auto">
          {filtered.length} appointment{filtered.length !== 1 ? 's' : ''} handled
        </span>
      </div>

      {/* Table */}
      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left border-collapse">
            <thead className="bg-gray-50 text-gray-500 uppercase text-xs tracking-wider">
              <tr>
                <th className="px-4 py-3">Appt #</th>
                <th
                  className="px-4 py-3 cursor-pointer select-none"
                  onClick={() => handleSort('appointmentDate')}
                >
                  <span className="flex items-center gap-1">
                    Date <SortIcon field="appointmentDate" />
                  </span>
                </th>
                <th className="px-4 py-3">Session</th>
                <th className="px-4 py-3">Patient</th>
                <th className="px-4 py-3">Phone</th>
                <th className="px-4 py-3">Doctor</th>
                <th className="px-4 py-3">Department</th>
                <th className="px-4 py-3">Token #</th>
                <th
                  className="px-4 py-3 cursor-pointer select-none"
                  onClick={() => handleSort('status')}
                >
                  <span className="flex items-center gap-1">
                    Status <SortIcon field="status" />
                  </span>
                </th>
                <th
                  className="px-4 py-3 cursor-pointer select-none"
                  onClick={() => handleSort('reviewedAt')}
                >
                  <span className="flex items-center gap-1">
                    Reviewed At <SortIcon field="reviewedAt" />
                  </span>
                </th>
                <th className="px-4 py-3">Checked In</th>
                <th className="px-4 py-3">Notes</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filtered.map((appt) => (
                <tr key={appt.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-3 font-mono text-xs font-semibold text-brand-700 whitespace-nowrap">
                    {appt.appointmentNumber}
                  </td>
                  <td className="px-4 py-3 text-gray-700 whitespace-nowrap">
                    {appt.appointmentDate
                      ? formatAppointmentDate(appt.appointmentDate)
                      : <span className="text-amber-600 text-xs font-medium">TBC</span>}
                  </td>
                  <td className="px-4 py-3">
                    {appt.sessionType ? (
                      <span className={clsx(
                        'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold',
                        SESSION_COLORS[appt.sessionType] ?? 'bg-gray-100 text-gray-600'
                      )}>
                        {appt.sessionType}
                      </span>
                    ) : (
                      <span className="text-xs text-gray-400">–</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-gray-900 font-medium">{appt.patientName}</td>
                  <td className="px-4 py-3 text-gray-700">{appt.patientPhone}</td>
                  <td className="px-4 py-3 text-gray-700">{appt.doctorName}</td>
                  <td className="px-4 py-3 text-gray-700">{appt.departmentName}</td>
                  <td className="px-4 py-3 text-gray-700 font-mono">
                    {appt.tokenNumber ?? '–'}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`status-${appt.status}`}>
                      {statusLabel(appt.status)}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-gray-700 whitespace-nowrap">
                    {formatDateTime(appt.reviewedAt)}
                  </td>
                  <td className="px-4 py-3 text-gray-700 whitespace-nowrap">
                    {formatTimeOnly(appt.checkedInAt)}
                  </td>
                  <td className="px-4 py-3 text-gray-500 max-w-[200px] truncate" title={appt.reviewNotes ?? ''}>
                    {appt.reviewNotes
                      ? appt.reviewNotes.length > 40
                        ? `${appt.reviewNotes.slice(0, 40)}...`
                        : appt.reviewNotes
                      : '–'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

// ── All Records Section ───────────────────────────────────────────────────────

const ALL_STATUSES = ['PENDING', 'CONFIRMED', 'CHECKED_IN', 'IN_CONSULTATION', 'COMPLETED', 'CANCELLED', 'REJECTED'];

function AllRecordsSection({
  data, isLoading, onRefresh,
  search, onSearchChange,
  statusFilter, onStatusChange,
  deptFilter, onDeptChange,
  doctorFilter, onDoctorChange,
  fromDate, onFromDateChange,
  toDate, onToDateChange,
  onViewLog,
}: {
  data: AllAppointmentDto[];
  isLoading: boolean;
  onRefresh: () => void;
  search: string; onSearchChange: (v: string) => void;
  statusFilter: string; onStatusChange: (v: string) => void;
  deptFilter: string; onDeptChange: (v: string) => void;
  doctorFilter: string; onDoctorChange: (v: string) => void;
  fromDate: string; onFromDateChange: (v: string) => void;
  toDate: string; onToDateChange: (v: string) => void;
  onViewLog: (id: number) => void;
}) {
  const depts = useMemo(() => [...new Set(data.map(a => a.departmentName))].sort(), [data]);
  const doctors = useMemo(() => {
    const map = new Map<number, string>();
    data.forEach(a => map.set(a.doctorId, a.doctorName));
    return [...map.entries()].sort((x, y) => x[1].localeCompare(y[1]));
  }, [data]);

  const defaultFrom = (() => { const d = new Date(); d.setDate(d.getDate() - 6); return d.toLocaleDateString('en-CA'); })();
  const defaultTo = new Date().toLocaleDateString('en-CA');
  const hasFilters = search || statusFilter || deptFilter || doctorFilter || fromDate !== defaultFrom || toDate !== defaultTo;

  return (
    <div className="space-y-4">
      {/* Filter bar */}
      <div className="card p-3 flex flex-wrap gap-2 items-center">
        <div className="relative">
          <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
          <input
            type="search"
            placeholder="Search patient, phone, appt #…"
            value={search}
            onChange={e => onSearchChange(e.target.value)}
            className="input pl-10 py-2.5 text-base h-12 w-56"
          />
        </div>

        <select value={statusFilter} onChange={e => onStatusChange(e.target.value)} className="input py-2.5 text-base h-12 w-40">
          <option value="">All Statuses</option>
          {ALL_STATUSES.map(s => <option key={s} value={s}>{statusLabel(s)}</option>)}
        </select>

        <select value={deptFilter} onChange={e => onDeptChange(e.target.value)} className="input py-2.5 text-base h-12 w-40">
          <option value="">All Departments</option>
          {depts.map(d => <option key={d} value={d}>{d}</option>)}
        </select>

        <select value={doctorFilter} onChange={e => onDoctorChange(e.target.value)} className="input py-2.5 text-base h-12 w-40">
          <option value="">All Doctors</option>
          {doctors.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
        </select>

        <div className="flex items-center gap-1">
          <input type="date" value={fromDate} onChange={e => onFromDateChange(e.target.value)}
            className="input py-2.5 text-base h-12 w-40" title="From date" />
          <span className="text-slate-400 text-xs">–</span>
          <input type="date" value={toDate} onChange={e => onToDateChange(e.target.value)}
            className="input py-2.5 text-base h-12 w-40" title="To date" />
        </div>

        {hasFilters && (
          <button
            onClick={() => {
              onSearchChange(''); onStatusChange(''); onDeptChange(''); onDoctorChange('');
              const today = new Date().toLocaleDateString('en-CA');
              const week = new Date(); week.setDate(week.getDate() - 6);
              onFromDateChange(week.toLocaleDateString('en-CA'));
              onToDateChange(today);
            }}
            className="text-xs text-brand-600 hover:text-brand-800 font-medium"
          >Clear all</button>
        )}

        <div className="ml-auto flex items-center gap-2">
          <span className="text-xs text-slate-500">{data.length} appointments</span>
          <button onClick={onRefresh} className="btn btn-sm btn-secondary" title="Refresh">
            <RefreshCw className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="space-y-2">{[1,2,3,4,5].map(i => <div key={i} className="skeleton h-14 rounded-xl" />)}</div>
      ) : data.length === 0 ? (
        <div className="text-center py-16">
          <ClipboardList className="w-10 h-10 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500">No appointments found</p>
          {hasFilters && <p className="text-xs text-slate-400 mt-1">Try adjusting the filters above</p>}
        </div>
      ) : (
        <div className="card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-100 bg-slate-50">
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Appt #</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Patient</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Doctor / Dept</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Date / Session</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Status</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Token</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Reviewed by</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Timeline</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Source</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {data.map((a) => (
                  <tr key={a.id} className="hover:bg-slate-50 transition-colors">
                    {/* Appt # */}
                    <td className="px-4 py-3">
                      <div className="font-mono text-xs font-semibold text-brand-700 bg-brand-50 px-2 py-0.5 rounded inline-block">
                        {a.appointmentNumber}
                      </div>
                      <div className="text-xs text-slate-400 mt-0.5">
                        {format(parseISO(a.createdAt), 'MMM d, h:mm a')}
                      </div>
                    </td>

                    {/* Patient */}
                    <td className="px-4 py-3">
                      <div className="font-medium text-slate-900">{a.patientName}</div>
                      <div className="text-xs text-slate-500">{a.patientPhone}</div>
                      {a.patientGender && <div className="text-xs text-slate-400">{a.patientGender}</div>}
                      {a.isFollowUp && <span className="text-xs text-purple-600 font-medium">Follow-up</span>}
                    </td>

                    {/* Doctor / Dept */}
                    <td className="px-4 py-3">
                      <div className="font-medium text-slate-800">{a.doctorName}</div>
                      <div className="text-xs text-slate-500">{a.departmentName}</div>
                    </td>

                    {/* Date / Session */}
                    <td className="px-4 py-3">
                      {a.appointmentDate ? (
                        <>
                          <div className="font-medium text-slate-800">
                            {format(parseISO(a.appointmentDate), 'EEE, MMM d yyyy')}
                          </div>
                          {a.sessionType && (
                            <span className={`text-xs px-1.5 py-0.5 rounded-full ${SESSION_COLORS[a.sessionType] ?? 'bg-slate-100 text-slate-600'}`}>
                              {a.sessionType}
                            </span>
                          )}
                        </>
                      ) : (
                        <span className="text-xs text-amber-600 font-medium">To be confirmed</span>
                      )}
                    </td>

                    {/* Status */}
                    <td className="px-4 py-3">
                      <span className={`status-${a.status}`}>{statusLabel(a.status)}</span>
                      {a.rejectionReason && (
                        <div className="text-xs text-red-500 mt-0.5 max-w-[160px] truncate" title={a.rejectionReason}>
                          {a.rejectionReason}
                        </div>
                      )}
                      {a.cancellationReason && (
                        <div className="text-xs text-slate-400 mt-0.5 max-w-[160px] truncate" title={a.cancellationReason}>
                          {a.cancellationReason}
                        </div>
                      )}
                    </td>

                    {/* Token */}
                    <td className="px-4 py-3">
                      {a.tokenNumber ? (
                        <span className="w-8 h-8 rounded-lg bg-brand-100 text-brand-700 font-bold text-sm flex items-center justify-center">
                          {a.tokenNumber}
                        </span>
                      ) : (
                        <span className="text-slate-300 text-xs">—</span>
                      )}
                    </td>

                    {/* Reviewed by */}
                    <td className="px-4 py-3">
                      {a.reviewedByName ? (
                        <>
                          <div className="text-sm font-medium text-slate-700">{a.reviewedByName}</div>
                          {a.reviewedAt && (
                            <div className="text-xs text-slate-400">
                              {format(parseISO(a.reviewedAt), 'MMM d, h:mm a')}
                            </div>
                          )}
                          {a.reviewNotes && (
                            <div className="text-xs text-slate-500 italic max-w-[140px] truncate" title={a.reviewNotes}>
                              "{a.reviewNotes}"
                            </div>
                          )}
                        </>
                      ) : (
                        <span className="text-slate-300 text-xs">—</span>
                      )}
                    </td>

                    {/* Timeline */}
                    <td className="px-4 py-3 text-xs text-slate-500 space-y-0.5">
                      {a.checkedInAt && (
                        <div title="Checked in">
                          ✓ In: {format(parseISO(a.checkedInAt), 'h:mm a')}
                        </div>
                      )}
                      {a.consultationStartAt && (
                        <div title="Consultation started">
                          ▶ Start: {format(parseISO(a.consultationStartAt), 'h:mm a')}
                        </div>
                      )}
                      {a.consultationEndAt && (
                        <div title="Consultation ended">
                          ■ End: {format(parseISO(a.consultationEndAt), 'h:mm a')}
                        </div>
                      )}
                      {a.cancelledAt && (
                        <div className="text-red-400" title={`Cancelled by ${a.cancelledBy}`}>
                          ✕ {format(parseISO(a.cancelledAt), 'MMM d, h:mm a')}
                          {a.cancelledBy && ` (${a.cancelledBy})`}
                        </div>
                      )}
                      {!a.checkedInAt && !a.consultationStartAt && !a.cancelledAt && (
                        <span className="text-slate-300">—</span>
                      )}
                    </td>

                    {/* Source */}
                    <td className="px-4 py-3">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${SOURCE_COLORS[a.bookingSource] ?? 'bg-slate-100 text-slate-600'}`}>
                        {a.bookingSource}
                      </span>
                    </td>

                    {/* Actions */}
                    <td className="px-4 py-3">
                      <button
                        onClick={() => onViewLog(a.id)}
                        className="btn btn-sm btn-ghost text-slate-500 whitespace-nowrap"
                        title="View activity log"
                      >
                        <History className="w-3.5 h-3.5" />
                        Log
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
